"use client";

import { c as _c } from "react/compiler-runtime";
import { createAuthClient } from "better-auth/client";
import { B3_API_URL } from "@/config/constants";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useUser } from "@b3dotfun/sdk/global-account/react";
import { getCachedSessionToken } from "@/lib/sessionRefresh";
import { extractEmail } from "@/lib/user/extractEmail";
import { IconArrowRight, IconArrowUpRight, IconPlugin2, IconShareOs, IconWallet3 } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18 } from "nucleo-ui-outline-18";
import { FaGithub, FaXTwitter, FaYoutube } from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GlassIcon, type GlassIconColor } from "@/components/ui/glass-icon";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { verifyIncentiveTask, type IncentiveTaskState } from "@/lib/api/incentive";
import { cn } from "@/lib/utils";
import type { TaskMeta } from "./tasks";
import { useIncentivePolling } from "./useIncentivePolling";
interface Props {
  task: IncentiveTaskState;
  meta: TaskMeta;
  onClaim: () => void;
  onOpenTreasury?: () => void;
  compact?: boolean;
  className?: string;
}
type ButtonState = "idle" | "loading" | "polling";
function GithubLogo(t0) {
  const $ = _c(2);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = <FaGithub className={className} aria-hidden="true" />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function XLogo(t0) {
  const $ = _c(2);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = <FaXTwitter className={className} aria-hidden="true" />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function YoutubeLogo(t0) {
  const $ = _c(2);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = <FaYoutube className={className} aria-hidden="true" />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const taskIconConfigByType: Record<string, {
  icon: ComponentType<{
    className?: string;
  }>;
  color: GlassIconColor;
}> = {
  add_connector: {
    icon: IconPlugin2,
    color: "blue"
  },
  share_workflow: {
    icon: IconShareOs,
    color: "blue"
  },
  fund_treasury: {
    icon: IconWallet3,
    color: "green"
  },
  follow_x: {
    icon: XLogo,
    color: "blue"
  },
  star_github: {
    icon: GithubLogo,
    color: "blue"
  },
  subscribe_youtube: {
    icon: YoutubeLogo,
    color: "red"
  }
};
function TaskGlassIcon(t0) {
  const $ = _c(5);
  const {
    type,
    claimed: t1
  } = t0;
  const claimed = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== type) {
    t2 = taskIconConfigByType[type] ?? {
      icon: IconPlugin2,
      color: "zinc" as const
    };
    $[0] = type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const config = t2;
  const t3 = claimed ? "green" : config.color;
  let t4;
  if ($[2] !== config.icon || $[3] !== t3) {
    t4 = <GlassIcon icon={config.icon} color={t3} size="md" />;
    $[2] = config.icon;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
const LINK_ERROR_CHANNEL = "b3-link-error";
const LINK_ERROR_TASK_PARAM = "link_error_for";
const LINK_ERROR_CODE_PARAM = "error";
const POPUP_BLOCKED_MSG = "Please allow popups for this site, then try again.";
interface LinkErrorBroadcast {
  taskType: string;
  errorCode: string;
}
function providerDisplayName(provider: string | undefined): string {
  switch (provider) {
    case "github":
      return "GitHub";
    case "twitter":
      return "X";
    case "google":
      return "Google";
    case "discord":
      return "Discord";
    case "microsoft":
      return "Microsoft";
    case "apple":
      return "Apple";
    case "slack":
      return "Slack";
    default:
      return "this account";
  }
}
function mapLinkError(provider: string | undefined, code: string): string {
  const name = providerDisplayName(provider);
  switch (code) {
    case "account_already_linked_to_different_user":
      return `This ${name} account is already connected to another B3 account. Sign in to a different ${name} account and try again.`;
    case "unable_to_get_user_info":
      return `Couldn't fetch your ${name} profile. Please try again in a moment.`;
    default:
      return `Couldn't connect your ${name} account. Please try again.`;
  }
}
function RewardBadges(t0) {
  const $ = _c(9);
  const {
    task
  } = t0;
  let t1;
  if ($[0] !== task.reward_cu) {
    t1 = task.reward_cu.toLocaleString();
    $[0] = task.reward_cu;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Badge variant="surface" className="text-2xs h-5 px-1.5 py-0 font-semibold tabular-nums">+{t1} CU</Badge>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== task.reward_ai_credits) {
    t3 = <Badge variant="surface" className="text-2xs h-5 px-1.5 py-0 font-semibold tabular-nums">+{task.reward_ai_credits} AI Credits</Badge>;
    $[4] = task.reward_ai_credits;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="mt-3 flex flex-wrap gap-1.5">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function getActionIcon(meta: TaskMeta) {
  if (meta.type === "fund_treasury") return IconWallet3;
  if (meta.isInternal) return IconArrowRight;
  return IconArrowUpRight;
}
export function IncentiveTaskCard({
  task,
  meta,
  onClaim,
  onOpenTreasury,
  compact = false,
  className
}: Props) {
  const [btnState, setBtnState] = useState<ButtonState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(false);
  const ActionIcon = getActionIcon(meta);
  const {
    user
  } = useUser();

  // Stable ref so the authClient useMemo (empty deps) can always read the
  // latest token without being recreated on every render.
  const baTokenRef = useRef<string | null>(null);

  // Created once per component mount. Uses baTokenRef so the onRequest
  // closure always sees the freshest token without needing authClient in
  // handleAction's useCallback deps.
  const authClient = useMemo(() => createAuthClient({
    baseURL: B3_API_URL,
    basePath: "/auth",
    fetchOptions: {
      credentials: "include",
      onRequest: ctx => {
        const token = baTokenRef.current;
        if (!token) return ctx;
        // ctx.headers is normally a Headers instance, but better-auth
        // plugin chains can pass a plain object. Handle both so the
        // Bearer token isn't silently dropped → falling back to cookies
        // (blocked cross-origin = the exact scenario this fix targets).
        if (ctx.headers instanceof Headers) {
          ctx.headers.set("Authorization", `Bearer ${token}`);
        } else if (ctx.headers && typeof ctx.headers === "object") {
          (ctx.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
        }
        return ctx;
      }
    }
  }), []);
  const triggerVerify = useCallback(async () => {
    setBtnState("loading");
    setErrorMsg(null);
    try {
      const data = await verifyIncentiveTask(task.type);
      if (data.status === "claimed") {
        onClaim();
      } else if (data.reason) {
        setErrorMsg(data.reason);
      }
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Network error. Please try again.");
    } finally {
      setBtnState("idle");
    }
  }, [task.type, onClaim]);

  // External tasks: smart polling kicks in only after user clicks button.
  // useCallback to keep handler refs stable across renders — without this,
  // useIncentivePolling's useEffect would re-run on every parent render and
  // reset the polling tier back to aggressive 2s.
  const handlePollClaimed = useCallback(() => {
    setBtnState("idle");
    onClaim();
  }, [onClaim]);
  // Keep polling alive across transient failures — user is mid-task and a
  // visibilitychange recheck after they return from YouTube/X/GitHub is what
  // actually claims the reward. Throttle hits ("Please wait before retrying")
  // are silent backend backoff, not actionable feedback for the user.
  // oauth_missing means the OAuth flow was cancelled or failed → reset to idle
  // so the user can retry without waiting for the polling timeout.
  const handlePollFailure = useCallback((resp: {
    reason?: string;
    reason_code?: string;
  }) => {
    if (resp.reason?.startsWith("Please wait")) return;
    if (resp.reason) setErrorMsg(resp.reason);
    if (resp.reason_code === "oauth_missing") setBtnState("idle");
  }, []);
  // Manual recheck affordance only shows after the polling cap (10 min) — by
  // then the user has had every chance to come back and visibilitychange to
  // fire a verify; if they're still here without a claim, give them an
  // explicit nudge.
  const handlePollTimeout = useCallback(() => {
    setBtnState("idle");
    setShowHint(true);
  }, []);
  useIncentivePolling({
    taskType: task.type,
    enabled: btnState === "polling",
    onClaimed: handlePollClaimed,
    onFailure: handlePollFailure,
    onTimeout: handlePollTimeout
  });

  // When the OAuth popup lands here with link_error_for + error params,
  // it's the popup that better-auth redirected after a link failure
  // (account_already_linked_to_different_user, unable_to_get_user_info, etc).
  // popup.opener was nulled before navigating cross-origin (see line ~271)
  // so postMessage isn't available — broadcast over a same-origin channel
  // so the main tab can react, then self-close.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const errorTaskType = params.get(LINK_ERROR_TASK_PARAM);
    const errorCode = params.get(LINK_ERROR_CODE_PARAM);
    if (!errorTaskType || !errorCode) return;
    try {
      const channel = new BroadcastChannel(LINK_ERROR_CHANNEL);
      const message: LinkErrorBroadcast = {
        taskType: errorTaskType,
        errorCode
      };
      channel.postMessage(message);
      channel.close();
    } catch {
      // Browser doesn't support BroadcastChannel — main tab can't be notified;
      // user will eventually see the polling-fallback message.
    }
    // Strip params so the URL is clean if window.close() fails (e.g. user
    // navigated here directly instead of via a script-opened popup).
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete(LINK_ERROR_TASK_PARAM);
    cleanUrl.searchParams.delete(LINK_ERROR_CODE_PARAM);
    window.history.replaceState(null, "", cleanUrl.toString());
    // Best-effort close. Allowed only when the window was opened by script
    // (i.e. the OAuth popup). On a regular tab this throws and is ignored.
    setTimeout(() => {
      try {
        window.close();
      } catch {
        // ignore
      }
    }, 50);
  }, []);

  // Main tab listens for link-error broadcasts from the popup. Each card
  // filters by its own task.type so only the matching card surfaces the
  // error. Setting btnState back to idle stops the polling that handleAction
  // started — without this, the verifier would eventually return
  // "Please connect your GitHub account first" and overwrite the real error.
  useEffect(() => {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;
    const channel_0 = new BroadcastChannel(LINK_ERROR_CHANNEL);
    const handler = (event: MessageEvent<LinkErrorBroadcast>) => {
      if (event.data?.taskType !== task.type) return;
      setErrorMsg(mapLinkError(meta.oauthProvider, event.data.errorCode));
      setBtnState("idle");
    };
    channel_0.addEventListener("message", handler);
    return () => {
      channel_0.removeEventListener("message", handler);
      channel_0.close();
    };
  }, [task.type, meta.oauthProvider]);
  const handleAction = useCallback(async () => {
    if (task.status === "claimed") return;
    if (meta.type === "fund_treasury" && onOpenTreasury) {
      // Verify first — if treasury is already funded, claim immediately and
      // skip the modal entirely. On any verify failure (balance 0, network,
      // throttle) fall through to opening the modal so the user can still
      // see the address to fund.
      setBtnState("loading");
      setErrorMsg(null);
      try {
        const data_0 = await verifyIncentiveTask(task.type);
        if (data_0.status === "claimed") {
          onClaim();
          setBtnState("idle");
          return;
        }
      } catch {
        // Network / unexpected error — degrade to opening the modal.
      }
      setBtnState("idle");
      onOpenTreasury();
      return;
    }
    if (meta.isInternal && meta.externalURL) {
      setBtnState("loading");
      setErrorMsg(null);
      // Reserve the tab synchronously inside the click handler — see the
      // OAuth branch below for the user-gesture rule. Moving window.open
      // after the verify await would trigger false popup-blocked errors on
      // Safari and Firefox.
      const popup = window.open("about:blank", "_blank");
      if (!popup) {
        setErrorMsg(POPUP_BLOCKED_MSG);
        setBtnState("idle");
        return;
      }
      try {
        const data_1 = await verifyIncentiveTask(task.type);
        if (data_1.status === "claimed") {
          popup.close();
          onClaim();
          setBtnState("idle");
          return;
        }
      } catch {
        // fall through and navigate the reserved popup.
      }
      popup.location.href = meta.externalURL;
      setBtnState("polling");
      return;
    }
    if (!meta.oauthProvider) return;

    // Unified OAuth flow for all external incentive tasks (follow_x, star_github,
    // subscribe_youtube):
    //   1. Verify first — claim immediately if already linked + action done.
    //   2. If oauth_missing → use linkSocial to link the provider account to the
    //      CURRENT betterauth session user (not email-matched user). The callback
    //      redirects the OAuth tab back to the current incentives surface with
    //      oauth_redirect_for=<task_type>, which opens the external URL so the
    //      user can complete the action. Main tab keeps polling.
    //   3. Already linked but action not done → open external URL directly.
    // linkSocial passes scopes when present (subscribe_youtube needs youtube.readonly);
    // undefined scopes are ignored by betterauth for providers that don't need them.
    setBtnState("loading");
    setErrorMsg(null);
    // Reserve a popup tab synchronously inside the click handler. Browsers
    // (Safari, Firefox, recent Chrome) block window.open() called later in
    // async callbacks because the user-gesture context is lost across awaits.
    // We open about:blank now and navigate it to the OAuth/external URL once
    // verify and linkSocial resolve. Closes the tab on early-return paths.
    // Cannot use noopener here — per WHATWG spec, noopener makes window.open
    // return null, which would trigger the "allow popups" error immediately.
    const popup_0 = window.open("about:blank", "_blank");
    if (!popup_0) {
      setErrorMsg(POPUP_BLOCKED_MSG);
      setBtnState("idle");
      return;
    }
    // Refresh the token ref so the stable authClient's onRequest always sends
    // the freshest (non-expired) BA session token as a Bearer header.
    baTokenRef.current = getCachedSessionToken();
    try {
      const data_2 = await verifyIncentiveTask(task.type);
      if (data_2.status === "claimed") {
        popup_0.close();
        onClaim();
        setBtnState("idle");
        return;
      }
      const needsOAuth = data_2.reason_code === "oauth_missing";
      if (needsOAuth) {
        const callbackUrl = new URL(window.location.href);
        callbackUrl.searchParams.set("oauth_redirect_for", task.type);
        // On link-side errors (account_already_linked_to_different_user,
        // unable_to_get_user_info) better-auth would otherwise redirect to
        // ${baseURL}/error = api.b3.fun/error — a dead end. Routing back to
        // this same page lets the popup broadcast the error to the main tab
        // (see useEffect below) and close itself.
        const errorUrl = new URL(window.location.href);
        errorUrl.searchParams.set(LINK_ERROR_TASK_PARAM, task.type);
        // loginHint pre-selects the matching Google account in the OAuth chooser.
        // Only Google supports this param; Twitter/GitHub ignore it.
        const loginHint = meta.oauthProvider === "google" ? extractEmail(user) : undefined;
        const result = await authClient.linkSocial({
          provider: meta.oauthProvider,
          scopes: meta.oauthScopes,
          callbackURL: callbackUrl.toString(),
          errorCallbackURL: errorUrl.toString(),
          disableRedirect: true,
          ...(loginHint ? {
            loginHint
          } : {})
        });
        const oauthUrl = (result as {
          data?: {
            url?: string;
          };
        }).data?.url;
        if (oauthUrl) {
          // Cut the opener reference before navigating cross-origin so the OAuth
          // provider page cannot navigate our main tab via window.opener.
          try {
            popup_0.opener = null;
          } catch {}
          popup_0.location.href = oauthUrl;
        } else {
          popup_0.close();
          setErrorMsg("Could not start sign-in. Please try again.");
          setBtnState("idle");
          return;
        }
      } else if (meta.externalURL) {
        try {
          popup_0.opener = null;
        } catch {}
        popup_0.location.href = meta.externalURL;
      } else {
        popup_0.close();
      }
      setBtnState("polling");
    } catch {
      popup_0.close();
      setErrorMsg("Something went wrong. Please try again.");
      setBtnState("idle");
    }
  }, [task.status, task.type, meta.type, meta.isInternal, meta.externalURL, meta.oauthProvider, meta.oauthScopes, onOpenTreasury, onClaim, user, authClient]);
  if (task.status === "claimed") {
    return <Card className={cn("h-full border-green-500/30 bg-green-500/5", className)}>
        <CardContent className={cn("flex h-full flex-col gap-3 p-5", compact && "p-4")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <TaskGlassIcon type={task.type} claimed />
              <div className="min-w-0">
                <h3 className="text-ds-text-primary truncate text-sm font-semibold">{meta.title}</h3>
                <p className="text-ds-text-muted mt-1 text-xs">{meta.description}</p>
                <RewardBadges task={task} />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 text-sm font-medium text-green-600">
              <IconCheckOutline18 className="h-4 w-4" /> Claimed
            </div>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card className={cn("hover:border-ds-brand-300 dark:hover:border-ds-brand-500/60 h-full transition-colors", className)}>
      <CardContent className={cn("flex h-full items-start gap-3 p-5", compact && "p-4")}>
        <TaskGlassIcon type={task.type} />
        <div className="flex min-w-0 flex-1 flex-col gap-4 self-stretch">
          <div className="min-w-0">
            <h3 className="text-ds-text-primary text-sm font-semibold leading-5">{meta.title}</h3>
            <p className={cn("text-ds-text-muted mt-1 leading-5", compact ? "text-xs" : "text-sm")}>
              {meta.description}
            </p>
            <RewardBadges task={task} />
          </div>

          {errorMsg && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
              {errorMsg}
            </div>}

          {/* Manual recheck affordance once polling has run past the relaxed tier. */}
          {showHint && <button onClick={() => void triggerVerify()} className="text-ds-brand-600 text-left text-xs underline">
              Followed but not detected? Tap to refresh.
            </button>}

          <Button disabled={btnState === "loading"} onClick={handleAction} variant={meta.isInternal ? "white" : "brand"} size="sm" className="mt-auto w-fit gap-2">
            {meta.isInternal ? meta.buttonTextInternal : meta.buttonTextLinked ?? meta.buttonTextNotLinked}
            {btnState === "loading" ? <PixelLoader size="sm" /> : <ActionIcon className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>;
}